/*
 * The mentor list (E2 `POST /batch-flow/mentors`, and the E5 `mentor` card) →
 * the view model the BatchFlow mentor components render. Both V2 trees consume
 * it, which is why it sits in Shared (code standard §7).
 *
 * This is the single place that knows the mentor object's shape. The backend
 * owns every rule — who is in which group, the top five, tier order,
 * availability, conflicts (R-05-01 to R-05-10) — so nothing here filters or
 * ranks; it only renames and formats.
 */

import { dayLabel } from "../SchedulePicker/constants";
import { toDisplayDate, toDisplayTime } from "./timings";

/** Mentor slots are always shown in IST, the student's zone beneath (R-05-09). */
const ADMIN_TIMEZONE_LABEL = "IST";

const OCCUPIED_REASON_LABELS = {
  "not-offered": "Mentor has not opened an RBT slot at this time",
  "outside-shift": "Outside the mentor's shift",
  held: "Already taken by another batch",
};

/**
 * @typedef {Object} MentorVM
 * @property {number} id
 * @property {string} name
 * @property {string|null} avatarUrl
 * @property {string|null} profileUrl
 * @property {string} tier - "P1" | "P2" | "P3", or "" with no tier row
 * @property {number|null} rating - 0–100, null = not rated
 * @property {string} shift
 * @property {number|null} activeBatches
 * @property {"available"|"occupied"|undefined} status - undefined on an unchecked search row
 * @property {number|null} conflictCount
 * @property {{day: string, dayLabel: string, from: string, reasonLabel: string}[]} occupiedTimings
 * @property {{from: string, to: string}[]} leaves - display dates
 * @property {"demo"|"current"|"past"|undefined} tag
 * @property {{day: string, dayLabel: string, times: {primary: string, secondary?: string}[]}[]} slots
 */

const availabilityStatus = (isAvailable) => {
  if (isAvailable === true) return "available";
  if (isAvailable === false) return "occupied";
  return undefined;
};

const occupiedReason = (timing) =>
  timing?.reason === "held" && timing?.batchId
    ? `Already taken by batch ${timing?.batchId}`
    : (OCCUPIED_REASON_LABELS?.[timing?.reason] ?? timing?.reason ?? "");

/* `studentLabel` is null when the student's zone is unknown; a second line that
   repeated IST would be worse than none. */
const toSlotTimes = (times, studentTimezoneLabel) =>
  (times ?? []).map((time) => ({
    primary: `${time?.istLabel ?? ""} ${ADMIN_TIMEZONE_LABEL}`.trim(),
    ...(time?.studentLabel && studentTimezoneLabel
      ? { secondary: `${time?.studentLabel} ${studentTimezoneLabel}` }
      : {}),
  }));

/**
 * One mentor object → MentorVM.
 * @param {object} raw - an E2 mentor object
 * @param {string} [studentTimezoneLabel] - e.g. "CDT"
 * @returns {MentorVM|null}
 */
export const mentorMapper = (raw, studentTimezoneLabel) =>
  raw
    ? {
        id: raw?.mentorId,
        name: raw?.name ?? "",
        avatarUrl: raw?.photoUrl ?? null,
        profileUrl: raw?.profileUrl ?? null,
        tier: raw?.tier ? `P${raw?.tier}` : "",
        rating: raw?.rating ?? null,
        shift: raw?.shift ?? "",
        activeBatches: raw?.activeBatches ?? null,
        status: availabilityStatus(raw?.isAvailable),
        conflictCount: raw?.conflictCount ?? null,
        occupiedTimings: (raw?.occupiedTimings ?? []).map((timing) => ({
          day: timing?.day,
          dayLabel: dayLabel(timing?.day),
          from: timing?.from,
          fromLabel: toDisplayTime(timing?.from),
          reasonLabel: occupiedReason(timing),
        })),
        leaves: (raw?.leaves ?? []).map((leave) => ({
          from: toDisplayDate(leave?.from),
          to: toDisplayDate(leave?.to),
        })),
        tag: raw?.tag,
        slots: (raw?.slots ?? []).map((slot) => ({
          day: slot?.day,
          dayLabel: dayLabel(slot?.day),
          times: toSlotTimes(slot?.times, studentTimezoneLabel),
        })),
      }
    : null;

/**
 * @typedef {Object} MentorListVM
 * @property {"known"|"bestMatched"|"search"} preselectedOption - the screen follows it (R-05-10)
 * @property {string} studentTimezoneLabel
 * @property {MentorVM|null} known
 * @property {MentorVM[]} bestMatched
 * @property {MentorVM[]} search
 */

/**
 * The E2 answer, in any of its three modes → MentorListVM.
 * @param {object} raw
 * @returns {MentorListVM}
 */
export const mentorListMapper = (raw) => {
  const label = raw?.studentTimezoneLabel ?? "";
  return {
    preselectedOption: raw?.preselectedOption ?? "search",
    studentTimezoneLabel: label,
    known: mentorMapper(raw?.known, label),
    bestMatched: (raw?.bestMatched ?? []).map((mentor) => mentorMapper(mentor, label)),
    search: (raw?.search ?? []).map((mentor) => mentorMapper(mentor, label)),
  };
};
